<template>
  <div class="lib-page">

    <!-- Page Header -->
    <div class="lib-page-header">
      <div>
        <h2 class="lib-page-title">Library</h2>
        <p class="lib-page-sub">All your uploaded files, organized by model</p>
      </div>
    </div>

    <div class="filters-wrapper">
      <div class="filters-bar" id="filtersBar"></div>
      
      <button @click="resetAllFilters" class="btn btn-sm btn-outline-danger hover-white">
        <span class="material-symbols-outlined fs-6">undo</span> Reset
      </button>
    </div>
<div id="genericFilterDropdownTemplate" style="display: none;">
  <div class="generic-filter-dropdown">
    <div class="dropdown-header">
      <input type="text" class="filter-search-input" placeholder="Search...">
    </div>
    <div class="dropdown-body filter-options-container"></div>
    <div class="dropdown-footer">
      <button class="filter-clear-btn">Clear</button>
      <button class="filter-apply-btn">Apply</button>
    </div>
  </div>
</div>
    <!-- Global Controls Bar -->
    <div class="lib-controls-bar">
      <div class="lib-search-box">
        <i class="fas fa-search" aria-hidden="true"></i>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search files…"
          @input="debouncedSearch"
        />
        <button v-if="searchQuery" class="lib-search-clear" @click="clearSearch" aria-label="Clear search">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="lib-controls-right">
        <span class="lib-total-badge">
          <i class="fas fa-paperclip"></i>
          {{ filteredData.length }} files
        </span>
        <div class="lib-view-toggle">
          <button class="lib-view-btn" :class="{ active: viewMode === 'grid' }" @click="viewMode = 'grid'" title="Grid view">
            <i class="fas fa-th-large"></i>
          </button>
          <button class="lib-view-btn" :class="{ active: viewMode === 'list' }" @click="viewMode = 'list'" title="List view">
            <i class="fas fa-list"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <LoadingBar v-if="loading" message="Loading Library…" />

    <!-- Content: one card per model group -->
    <div v-else-if="groupedData.length > 0" class="lib-content">
      <div
        v-for="(group, idx) in groupedData"
        :key="group.key"
        class="lib-cat-card"
      >
        <!-- ── Category Card Header ── -->
        <div class="lib-cat-card-header">
          <div class="lib-cat-icon" :style="catIconStyle(idx)">
            <i class="fas fa-layer-group" aria-hidden="true"></i>
          </div>
          <div class="lib-cat-meta">
         <span class="lib-cat-name">{{ (group.key || '').toLowerCase().includes('company_user') ? 'Vendors' : group.label }}</span>
            <span class="lib-cat-count">{{ group.files.length }} {{ group.files.length === 1 ? 'file' : 'files' }}</span>
          </div>
        </div>

        <!-- ── Per-Category Nav Tabs (built from attachment_type values) ── -->
        <div class="lib-cat-tabs-bar">
          <!-- "All" tab always first -->
          <button
            class="lib-cat-tab"
            :class="{ active: (activeCatTab[group.key] || 'all') === 'all' }"
            @click="setCatTab(group.key, 'all')"
          >
            <i class="fas fa-border-all"></i>
            All
            <span class="lib-cat-tab-count">{{ group.files.length }}</span>
          </button>

          <!-- One tab per distinct attachment_type in this group -->
          <button
            v-for="typeTab in group.typeTabs"
            :key="typeTab.key"
            class="lib-cat-tab"
            :class="{ active: (activeCatTab[group.key] || 'all') === typeTab.key }"
            @click="setCatTab(group.key, typeTab.key)"
          >
            <i :class="typeTabIcon(typeTab.key)" aria-hidden="true"></i>
            {{ typeTab.label }}
            <span class="lib-cat-tab-count">{{ typeTab.count }}</span>
          </button>
        </div>

        <!-- ── File Grid ── -->
        <div class="lib-cat-body">
          <template v-if="viewMode === 'grid'">
            <div class="lib-file-grid">
              <div
                v-for="att in getCatFiles(group)"
                :key="att.id"
                class="lib-file-card"
                @click="openPreview(att)"
              >
                <!-- Thumbnail -->
                <div class="lib-file-thumb" :class="'thumb-' + getEffectiveType(att)">
                  <img
                    v-if="getEffectiveType(att) === 'image' && att.file_url"
                    :src="att.file_url"
                    :alt="att.file_name"
                    loading="lazy"
                    class="lib-thumb-img"
                  />
                  <i v-else :class="fileIcon(att)" class="lib-thumb-icon" aria-hidden="true"></i>
                </div>

                <!-- Card Body -->
                <div class="lib-file-body">
                  <div class="lib-file-body-top">
                    <span class="lib-fmt-pill" :class="'fmt-' + getEffectiveType(att)">
                      {{ getEffectiveType(att).toUpperCase() }}
                    </span>
                    <span v-if="att.attachment_type" class="lib-atype-badge">
                      {{ att.attachment_type }}
                    </span>
                  </div>
                  <p class="lib-file-name" :title="att.file_name">{{ att.file_name }}</p>
                  <p class="lib-file-meta">{{ fmtSize(att.file_size) }} · {{ formatDate(att.created_at) }}</p>
                </div>

                <!-- Card Actions -->
                <div class="lib-file-actions" @click.stop>
                  <button class="lib-fab lib-fab-eye" @click="openPreview(att)" :aria-label="'Preview ' + att.file_name">
                    <i class="fas fa-eye"></i> Preview
                  </button>
                  <a :href="att.file_url" :download="att.file_name" class="lib-fab lib-fab-dl" @click.stop :aria-label="'Download ' + att.file_name">
                    <i class="fas fa-download"></i>
                  </a>
                </div>
              </div>

              <!-- Empty within tab -->
              <div v-if="getCatFiles(group).length === 0" class="lib-tab-empty">
                <i class="fas fa-inbox"></i>
                <span>No files in this category</span>
              </div>
            </div>
          </template>

          <!-- ── List View ── -->
          <template v-else>
            <div class="lib-file-list">
              <div
                v-for="att in getCatFiles(group)"
                :key="att.id"
                class="lib-list-row"
                @click="openPreview(att)"
              >
                <div class="lib-list-icon" :class="'thumb-' + getEffectiveType(att)">
                  <img
                    v-if="getEffectiveType(att) === 'image' && att.file_url"
                    :src="att.file_url"
                    :alt="att.file_name"
                    loading="lazy"
                    class="lib-list-img"
                  />
                  <i v-else :class="fileIcon(att)" aria-hidden="true"></i>
                </div>

                <div class="lib-list-name">
                  <span class="lib-file-name-row">{{ att.file_name }}</span>
                  <span v-if="att.external_id" class="lib-file-eid">{{ att.external_id }}</span>
                </div>

                <div class="lib-list-badges">
                  <span class="lib-fmt-pill" :class="'fmt-' + getEffectiveType(att)">
                    {{ getEffectiveType(att).toUpperCase() }}
                  </span>
                  <span v-if="att.attachment_type" class="lib-atype-badge">
                    {{ att.attachment_type }}
                  </span>
                </div>

                <span class="lib-list-size">{{ fmtSize(att.file_size) }}</span>
                <span class="lib-list-date">{{ formatDate(att.created_at) }}</span>

                <div class="lib-file-actions" @click.stop>
                  <button class="lib-fab lib-fab-eye" @click.stop="openPreview(att)"><i class="fas fa-eye"></i></button>
                  <a :href="att.file_url" :download="att.file_name" class="lib-fab lib-fab-dl" @click.stop><i class="fas fa-download"></i></a>
                </div>
              </div>

              <div v-if="getCatFiles(group).length === 0" class="lib-tab-empty">
                <i class="fas fa-inbox"></i>
                <span>No files in this category</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="lib-empty">
      <div class="lib-empty-icon"><i class="fas fa-folder-open"></i></div>
      <p class="lib-empty-title">No files found</p>
      <p class="lib-empty-sub">{{ searchQuery ? 'Try a different search term.' : 'Upload files to get started.' }}</p>
      <button v-if="searchQuery" class="lib-empty-btn" @click="clearSearch">Clear search</button>
    </div>

    <!-- Footer -->
    <div v-if="!loading && groupedData.length > 0" class="lib-footer">
      <span class="lib-footer-total">
        Showing <strong>{{ filteredData.length }}</strong> of <strong>{{ pagination.total_rows ?? 0 }}</strong> files
      </span>
      <Pagination :pagination="pagination" @page-change="changePage" />
    </div>

    <!-- Preview Modal -->
    <FormModal ref="previewModal" :title="previewFile ? previewFile.file_name : 'Preview'" id="attachment_preview_modal">
      <div v-if="previewFile" class="preview-body">

        <template v-if="getEffectiveType(previewFile) === 'image'">
          <div class="preview-media-wrap">
            <img :src="previewFile.file_url" :alt="previewFile.file_name" class="preview-img" />
          </div>
        </template>

        <template v-else-if="getEffectiveType(previewFile) === 'pdf'">
          <iframe :src="previewFile.file_url" class="preview-iframe"></iframe>
        </template>

        <template v-else-if="getEffectiveType(previewFile) === 'video'">
          <div class="preview-media-wrap">
            <video :src="previewFile.file_url" controls class="preview-video"></video>
          </div>
        </template>

        <template v-else>
          <div class="preview-fallback">
            <div class="preview-fallback-icon" :class="'thumb-' + getEffectiveType(previewFile)">
              <i :class="fileIcon(previewFile)"></i>
            </div>
            <p class="preview-fallback-name">{{ previewFile.file_name }}</p>
            <p class="preview-fallback-sub">Preview not available for this file type</p>
            <a :href="previewFile.file_url" :download="previewFile.file_name" class="preview-dl-btn">
              <i class="fas fa-download"></i> Download to view
            </a>
          </div>
        </template>

        <div class="preview-meta-strip">
          <div class="preview-meta-left">
            <span class="lib-fmt-pill" :class="'fmt-' + getEffectiveType(previewFile)">
              {{ getEffectiveType(previewFile).toUpperCase() }}
            </span>
            <span
              class="lib-type-pill"
              :style="{ background: typeColorBg(previewFile.attachable_type), color: typeColorText(previewFile.attachable_type) }"
            >
              <i class="fas fa-layer-group" style="font-size:10px;margin-right:3px;"></i>
              {{ parseModelName((previewFile.attachable_type || '').split('\\').pop()) }}
            </span>
            <span v-if="previewFile.attachment_type" class="lib-atype-badge">{{ previewFile.attachment_type }}</span>
            <span class="preview-meta-detail">{{ fmtSize(previewFile.file_size) }}</span>
            <span class="preview-meta-detail">{{ formatDate(previewFile.created_at) }}</span>
          </div>
          <a :href="previewFile.file_url" :download="previewFile.file_name" class="preview-dl-btn-sm">
            <i class="fas fa-download"></i> Download
          </a>
        </div>
      </div>
    </FormModal>

  </div>
</template>
<script>
import { form_api_submit } from '@/utils/FormSubmit'
import Pagination from '@/components/utils/PaginationBar.vue'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'

export default {
  name: 'AttachmentLibrary',
  components: { Pagination, LoadingBar, FormModal },

  data() {
    return {
      attachments: [],
      pagination: { total_rows: 0, per_page: 50, current_page: 1, total_pages: 1 },
      loading: false,
      previewFile: null,
      searchQuery: '',
      debounceTimer: null,
      viewMode: 'grid',
      currentFilters: {},
      filterManager: null,

      filterData: {
        users: [],
        locations: [],
        purchase_orders: [],
        vendors: [],           // ← now a top-level field from data.vendors
      },

      activeCatTab: {},

      catPalette: [
        { bg: '#EEEDFE', color: '#3C3489' },
        { bg: '#E1F5EE', color: '#085041' },
        { bg: '#FCEBEB', color: '#791F1F' },
        { bg: '#FAEEDA', color: '#633806' },
        { bg: '#E6F1FB', color: '#0C447C' },
        { bg: '#FBEAF0', color: '#72243E' },
        { bg: '#EAF3DE', color: '#27500A' },
        { bg: '#F1EFE8', color: '#444441' },
      ],
    }
  },

  computed: {
    filteredData() {
      let list = Array.isArray(this.attachments) ? [...this.attachments] : []

      if (this.currentFilters.search && list.length > 0) {
        const q = this.currentFilters.search.toLowerCase()
        list = list.filter(a => (a.file_name || '').toLowerCase().includes(q))
      }

      return list
    },

    groupedData() {
      const map = {}
      const data = this.filteredData || []

      data.forEach(att => {
        const raw = att.attachable_type || ''
        const parts = raw.split('\\')
        const key = parts[parts.length - 1] || 'uncategorized'
        if (!map[key]) map[key] = []
        map[key].push(att)
      })

      return Object.entries(map).map(([key, files]) => {
        const typeCounts = {}
        files.forEach(f => {
          const t = f.attachment_type || 'Other'
          typeCounts[t] = (typeCounts[t] || 0) + 1
        })
        const typeTabs = Object.entries(typeCounts).map(([label, count]) => ({
          key:   label,
          label: label,
          count,
        }))

        return {
          key,
          label: this.parseModelName(key),
          files,
          typeTabs,
          typeSummary: this.buildTypeSummary(files),
        }
      })
    },
  },

 mounted() {
  this.fetchAttachments()
},

  methods: {
   
  initFilters() {
    if (typeof $ === 'undefined' || typeof GenericFilterManager === 'undefined') {
      console.warn('GenericFilterManager not available')
      return
    }

    const userOptions = (this.filterData.users || []).map(u => ({
      value: String(u.id),
      label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || `User ${u.id}`
    }))

    const locationOptions = (this.filterData.locations || []).map(l => ({
      value: String(l.id),
      label: `${l.code || ''} - ${l.name || ''}`.trim() || `Location ${l.id}`
    }))

    const purchaseOrderOptions = (this.filterData.purchase_orders || []).map(po => ({
      value: String(po.id),
      label: po.external_id ? `${po.external_id}` : `PO #${po.id}`
    }))

    const vendorOptions = (this.filterData.vendors || []).map(v => ({
      value: String(v.id),
      label: `${v.first_name || ''}${v.last_name ? ' ' + v.last_name : ''}`.trim() || `Vendor ${v.id}`
    }))

    this.filterManager = new GenericFilterManager({
      containerId: 'filtersBar',
      filters: [
        {
          key:     'attachment_type',
          label:   'Attachment Type',
          type:    'checkbox',
          options: [
            { value: 'Document',         label: 'Document'         },
            { value: 'image',            label: 'Image'            },
            { value: 'Order Attachment', label: 'Order Attachment' },
          ]
        },
        {
          key:     'file_type',
          label:   'File Format',
          type:    'checkbox',
          options: [
            { value: 'pdf',      label: 'PDF'      },
            { value: 'image',    label: 'Image'    },
            { value: 'document', label: 'Document' },
            { value: 'video',    label: 'Video'    },
            { value: 'file',     label: 'File'     },
            { value: 'other',    label: 'Other'    },
          ]
        },
        {
          key:     'attachable_type',
          label:   'Model Type',
          type:    'checkbox',
          options: [
            { value: 'App\\Models\\company_user',   label: 'Users'           },
            { value: 'App\\Models\\location',       label: 'Locations'       },
            { value: 'App\\Models\\purchase_order', label: 'Purchase Orders' },
          ]
        },
        {
          key:     'user_id',
          label:   'User',
          type:    'checkbox',
          options: userOptions.length ? userOptions : [{ value: '', label: 'No users available' }]
        },
        {
          key:     'location_id',
          label:   'Location',
          type:    'checkbox',
          options: locationOptions.length ? locationOptions : [{ value: '', label: 'No locations available' }]
        },
        {
          key:     'purchase_order_id',
          label:   'Purchase Order',
          type:    'checkbox',
          options: purchaseOrderOptions.length ? purchaseOrderOptions : [{ value: '', label: 'No purchase orders available' }]
        },
        {
          key:     'vendor_id',
          label:   'Vendor',
          type:    'checkbox',
          options: vendorOptions.length ? vendorOptions : [{ value: '', label: 'No vendors available' }]
        },
        {
          key:         'date_from',
          label:       'Date From',
          type:        'date',
          placeholder: 'Start date'
        },
        {
          key:         'date_to',
          label:       'Date To',
          type:        'date',
          placeholder: 'End date'
        },
      ],
      onFilterChange: (filters) => this.applyFilters(filters)
    })
  },
   
  applyFilters(activeFilters) {
    this.currentFilters = { ...activeFilters }
    this.pagination.current_page = 1
    this.fetchAttachments()
  },

  resetAllFilters() {
    if (this.filterManager) this.filterManager.clearAllFilters()
    this.currentFilters = {}
    this.pagination.current_page = 1
    this.fetchAttachments()
  },
    /* ─── API ──────────────────────────────────────────────── */
    async fetchAttachments() {
    this.loading = true

    const requestData = {
      page:     this.pagination.current_page,
      per_page: this.pagination.per_page,
    }

    // Only add filters if they have values
    if (this.currentFilters.search)           requestData.search           = this.currentFilters.search
    if (this.currentFilters.attachment_type)  requestData.attachment_type  = [].concat(this.currentFilters.attachment_type)
    if (this.currentFilters.file_type)        requestData.file_type        = [].concat(this.currentFilters.file_type)
    if (this.currentFilters.attachable_type)  requestData.attachable_type  = [].concat(this.currentFilters.attachable_type)
    if (this.currentFilters.user_id)          requestData.user_id          = [].concat(this.currentFilters.user_id)
    if (this.currentFilters.location_id)      requestData.location_id      = [].concat(this.currentFilters.location_id)
    if (this.currentFilters.purchase_order_id)requestData.purchase_order_id= [].concat(this.currentFilters.purchase_order_id)
    if (this.currentFilters.vendor_id)        requestData.vendor_id        = [].concat(this.currentFilters.vendor_id)
    if (this.currentFilters.date_from)        requestData.date_from        = this.currentFilters.date_from
    if (this.currentFilters.date_to)          requestData.date_to          = this.currentFilters.date_to

    try {
      const result = await form_api_submit({
        url:                'library/list',
        method:             'GET',
        data:               requestData,
        show_success_toast: false,
        show_error_toast:   true,
        remove_pre_toasts:  false,
      })

      if (result.success && result.data) {
        const listData    = result.data.list || {}
        this.attachments  = Array.isArray(listData.list) ? listData.list : []
        this.pagination   = listData.pagination || { total_rows: 0, per_page: 50, current_page: 1, total_pages: 1 }

        // Store filter dropdown data from API
        if (result.data.users)     this.filterData.users           = result.data.users
        if (result.data.locations) this.filterData.locations       = result.data.locations
        if (result.data.orders)    this.filterData.purchase_orders = result.data.orders
        if (result.data.vendors)   this.filterData.vendors         = result.data.vendors

        // ── KEY FIX ──────────────────────────────────────────
        // Only init filters ONCE on first load (filterManager is null).
        // Never destroy+recreate on subsequent fetches — that breaks dropdowns.
        // Matches materialList pattern: Promise.all → initFilters() once.
        if (!this.filterManager) {
          this.$nextTick(() => {
            setTimeout(() => this.initFilters(), 100)
          })
        }
        // ─────────────────────────────────────────────────────

      } else {
        this.attachments = []
      }
    } catch (error) {
      console.error('Error fetching attachments:', error)
      this.attachments = []
    } finally {
      this.loading = false
    }
  },

    /* ─── Rest of methods ─────────────────────────────────── */
    setCatTab(groupKey, tabKey) {
      this.activeCatTab[groupKey] = tabKey
    },

    getCatFiles(group) {
      const active = this.activeCatTab[group.key] || 'all'
      if (active === 'all') return group.files || []
      return (group.files || []).filter(f => (f.attachment_type || 'Other') === active)
    },

    isPdfByName(att) {
      return /\.pdf$/i.test(att?.file_name || '')
    },

    getEffectiveType(att) {
      if (!att) return 'other'
      if (this.isPdfByName(att)) return 'pdf'
      return att.file_type || 'other'
    },

    fileIcon(att) {
      const map = {
        pdf:      'fas fa-file-pdf',
        image:    'fas fa-image',
        document: 'fas fa-file-word',
        video:    'fas fa-file-video',
        file:     'fas fa-file-alt',
      }
      return map[this.getEffectiveType(att)] || 'fas fa-file'
    },

    typeTabIcon(key) {
      if (!key) return 'fas fa-file'
      const k = key.toLowerCase()
      if (k.includes('image') || k.includes('photo'))    return 'fas fa-image'
      if (k.includes('video'))                           return 'fas fa-film'
      if (k.includes('pdf'))                             return 'fas fa-file-pdf'
      if (k.includes('doc') || k.includes('word'))       return 'fas fa-file-word'
      if (k.includes('sheet') || k.includes('excel'))    return 'fas fa-file-excel'
      if (k.includes('contract') || k.includes('legal')) return 'fas fa-file-contract'
      if (k.includes('invoice') || k.includes('bill'))   return 'fas fa-file-invoice'
      if (k.includes('report'))                          return 'fas fa-chart-bar'
      return 'fas fa-tag'
    },

    parseModelName(raw) {
      if (!raw) return 'Uncategorized'
      let name = raw
      if (name.includes('_')) {
        name = name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      } else {
        name = name.replace(/([A-Z])/g, ' $1').trim()
      }
      return name.replace(/\b\w/g, c => c.toUpperCase())
    },

    formatLabel(str) {
      if (!str) return 'Uncategorized'
      return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },

    formatDate(dateString) {
      if (!dateString) return '—'
      return new Date(dateString).toLocaleDateString('en-US', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    },

    fmtSize(b) {
      if (!b) return '—'
      if (b < 1024)    return b + ' B'
      if (b < 1048576) return Math.round(b / 1024) + ' KB'
      return (b / 1048576).toFixed(1) + ' MB'
    },

    catIconStyle(idx) {
      return this.catPalette[idx % this.catPalette.length]
    },

    buildTypeSummary(files) {
      const counts = {}
      ;(files || []).forEach(f => {
        const t = this.getEffectiveType(f)
        counts[t] = (counts[t] || 0) + 1
      })
      return Object.entries(counts).map(([type, count]) => ({ type, count }))
    },

    typeColorBg(type) {
      const p = ['#EEEDFE','#E1F5EE','#FCEBEB','#FAEEDA','#E6F1FB','#FBEAF0','#EAF3DE','#F1EFE8']
      return p[Math.abs([...(type ?? '')].reduce((a, c) => a + c.charCodeAt(0), 0)) % p.length]
    },

    typeColorText(type) {
      const p = ['#3C3489','#085041','#791F1F','#633806','#0C447C','#72243E','#27500A','#444441']
      return p[Math.abs([...(type ?? '')].reduce((a, c) => a + c.charCodeAt(0), 0)) % p.length]
    },

    debouncedSearch() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.currentFilters.search = this.searchQuery || undefined
        this.pagination.current_page = 1
        this.fetchAttachments()
      }, 350)
    },

    clearSearch() {
      this.searchQuery = ''
      this.currentFilters.search = undefined
      this.pagination.current_page = 1
      this.fetchAttachments()
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.total_pages) return
      this.pagination.current_page = page
      this.fetchAttachments()
    },

    openPreview(att) {
      this.previewFile = att
      this.$refs.previewModal.showModal()
    },
  },
}
</script>
<style scoped>
/* ── Page ──────────────────────────────────────────────────── */
.lib-page {
  padding: 1.5rem;
  min-height: 100vh;
  background: #f6f7fb;
}

.lib-page-header {
  margin-bottom: 1.25rem;
}

.lib-page-title {
  font-size: 22px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 2px;
  letter-spacing: -0.3px;
}

.lib-page-sub {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

/* ── Global Controls Bar ───────────────────────────────────── */
.lib-controls-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 14px;
  margin-bottom: 1.5rem;
}

.lib-controls-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.lib-total-badge {
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 5px;
}

.lib-total-badge i {
  color: #9ca3af;
}

/* Search */
.lib-search-box {
  display: flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 10px;
  height: 34px;
  background: #f9fafb;
  transition: border-color .15s;
  min-width: 220px;
}

.lib-search-box:focus-within {
  border-color: #a5b4fc;
  background: #fff;
}

.lib-search-box i { color: #9ca3af; font-size: 13px; flex-shrink: 0; }

.lib-search-box input {
  border: none;
  outline: none;
  font-size: 13px;
  background: transparent;
  flex: 1;
  color: #111827;
}

.lib-search-box input::placeholder { color: #9ca3af; }

.lib-search-clear {
  border: none;
  background: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  font-size: 11px;
  line-height: 1;
}

.lib-search-clear:hover { color: #374151; }

/* View toggle */
.lib-view-toggle {
  display: flex;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.lib-view-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #f9fafb;
  color: #9ca3af;
  cursor: pointer;
  font-size: 13px;
  transition: all .15s;
}

.lib-view-btn:first-child { border-right: 1px solid #e5e7eb; }
.lib-view-btn.active { background: #4f46e5; color: #fff; }
.lib-view-btn:hover:not(.active) { background: #f3f4f6; color: #374151; }

/* ── Category Card ─────────────────────────────────────────── */
.lib-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.lib-cat-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
}

/* Card Header */
.lib-cat-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px 0;
}

.lib-cat-icon {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
}

.lib-cat-meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.lib-cat-name {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
}

.lib-cat-count {
  font-size: 12px;
  color: #6b7280;
}

/* ── Per-Category Nav Tabs ─────────────────────────────────── */
.lib-cat-tabs-bar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 12px 18px 0;
  border-bottom: 1px solid #f3f4f6;
  overflow-x: auto;
  scrollbar-width: none;
}

.lib-cat-tabs-bar::-webkit-scrollbar { display: none; }

.lib-cat-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  font-size: 12.5px;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  margin-bottom: -1px;
  transition: color .15s, border-color .15s;
  border-radius: 0;
}

.lib-cat-tab i {
  font-size: 11px;
}

.lib-cat-tab:hover {
  color: #374151;
}

.lib-cat-tab.active {
  color: #4f46e5;
  border-bottom-color: #4f46e5;
}

.lib-cat-tab-count {
  font-size: 10.5px;
  font-weight: 600;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 20px;
  padding: 1px 6px;
  line-height: 1.6;
}

.lib-cat-tab.active .lib-cat-tab-count {
  background: #eef2ff;
  color: #4f46e5;
}

/* ── Card Body ─────────────────────────────────────────────── */
.lib-cat-body {
  padding: 16px 18px 18px;
}

/* ── File Grid ─────────────────────────────────────────────── */
.lib-file-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(165px, 1fr));
  gap: 12px;
}

.lib-file-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: border-color .15s, box-shadow .15s, transform .1s;
}

.lib-file-card:hover {
  border-color: #c7d2fe;
  box-shadow: 0 4px 14px rgba(79,70,229,.08);
  transform: translateY(-2px);
}

/* Thumb */
.lib-file-thumb {
  width: 100%;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.lib-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lib-thumb-icon { font-size: 30px; }

.thumb-pdf      { background: #fef2f2; color: #991b1b; }
.thumb-image    { background: #f0fdf4; color: #166534; }
.thumb-document { background: #eff6ff; color: #1e40af; }
.thumb-video    { background: #fffbeb; color: #92400e; }
.thumb-file     { background: #f0fdf4; color: #166534; }
.thumb-other    { background: #f9fafb; color: #4b5563; }

/* Body */
.lib-file-body {
  padding: 9px 11px 6px;
  flex: 1;
}

.lib-file-body-top {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  margin-bottom: 5px;
}

.lib-file-name {
  font-size: 12px;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0 0 3px;
}

.lib-file-meta {
  font-size: 11px;
  color: #9ca3af;
  margin: 0;
}

/* Actions */
.lib-file-actions {
  display: flex;
  gap: 5px;
  padding: 8px 11px;
  border-top: 1px solid #f3f4f6;
}

.lib-fab {
  flex: 1;
  height: 27px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  font-size: 11px;
  text-decoration: none;
  transition: all .12s;
}

.lib-fab:hover { background: #f3f4f6; border-color: #d1d5db; }
.lib-fab-eye { color: #4f46e5; }
.lib-fab-dl  { color: #059669; flex: 0 0 27px; }

/* ── Pills ─────────────────────────────────────────────────── */
.lib-fmt-pill {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 20px;
  display: inline-block;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.fmt-pdf      { background: #fef2f2; color: #991b1b; }
.fmt-image    { background: #f0fdf4; color: #166534; }
.fmt-document { background: #eff6ff; color: #1e40af; }
.fmt-video    { background: #fffbeb; color: #92400e; }
.fmt-file     { background: #f0fdf4; color: #166534; }
.fmt-other    { background: #f9fafb; color: #4b5563; }

.lib-atype-badge {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 20px;
  background: #eef2ff;
  color: #4f46e5;
  display: inline-block;
  white-space: nowrap;
}

.lib-type-pill {
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
}

/* ── List View ─────────────────────────────────────────────── */
.lib-file-list {
  display: flex;
  flex-direction: column;
}

.lib-list-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 4px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  border-radius: 8px;
  transition: background .1s;
}

.lib-list-row:last-child { border-bottom: none; }
.lib-list-row:hover { background: #f9fafb; }

.lib-list-icon {
  width: 34px;
  height: 34px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
  overflow: hidden;
}

.lib-list-img { width: 100%; height: 100%; object-fit: cover; }

.lib-list-name {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lib-file-name-row {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lib-file-eid {
  font-size: 11px;
  color: #9ca3af;
  font-family: monospace;
}

.lib-list-badges {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}

.lib-list-size,
.lib-list-date {
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 60px;
}

/* ── Tab Empty ─────────────────────────────────────────────── */
.lib-tab-empty {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 2.5rem;
  color: #9ca3af;
  font-size: 13px;
}

.lib-tab-empty i { font-size: 20px; }

/* ── Empty State ───────────────────────────────────────────── */
.lib-empty {
  text-align: center;
  padding: 4rem 2rem;
}

.lib-empty-icon {
  width: 64px;
  height: 64px;
  background: #f3f4f6;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: #9ca3af;
  margin-bottom: 1rem;
}

.lib-empty-title { font-size: 16px; font-weight: 600; color: #374151; margin: 0 0 6px; }
.lib-empty-sub   { font-size: 13px; color: #9ca3af; margin: 0 0 1.25rem; }

.lib-empty-btn {
  background: #4f46e5;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.lib-empty-btn:hover { background: #4338ca; }

/* ── Footer ────────────────────────────────────────────────── */
.lib-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 18px;
  margin-top: 1rem;
}

.lib-footer-total { font-size: 13px; color: #6b7280; }
.lib-footer-total strong { color: #111827; }

/* ── Preview Modal ─────────────────────────────────────────── */
.preview-body { min-height: 200px; }

.preview-media-wrap { border-radius: 10px; overflow: hidden; background: #f3f4f6; }

.preview-img   { width: 100%; max-height: 65vh; object-fit: contain; display: block; }
.preview-video { width: 100%; max-height: 65vh; display: block; border-radius: 10px; }

.preview-iframe {
  width: 100%; height: 65vh;
  border: none; display: block; border-radius: 10px;
}

.preview-fallback {
  text-align: center;
  padding: 3rem 2rem;
}

.preview-fallback-icon {
  width: 64px; height: 64px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  margin-bottom: 1rem;
}

.preview-fallback-name { font-size: 14px; font-weight: 500; color: #111827; margin: 0 0 4px; }
.preview-fallback-sub  { font-size: 12px; color: #9ca3af; margin: 0 0 1.25rem; }

.preview-dl-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: #4f46e5; color: #fff; border-radius: 8px;
  padding: 8px 18px; font-size: 13px; font-weight: 500;
  text-decoration: none; transition: background .15s;
}

.preview-dl-btn:hover { background: #4338ca; color: #fff; }

.preview-meta-strip {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 10px;
  margin-top: 14px; padding-top: 14px;
  border-top: 1px solid #f3f4f6;
}

.preview-meta-left {
  display: flex; align-items: center;
  gap: 8px; flex-wrap: wrap;
}

.preview-meta-detail { font-size: 12px; color: #9ca3af; }

.preview-dl-btn-sm {
  display: inline-flex; align-items: center; gap: 5px;
  background: #4f46e5; color: #fff; border-radius: 7px;
  padding: 6px 14px; font-size: 12px; font-weight: 500;
  text-decoration: none; transition: background .15s;
}

.preview-dl-btn-sm:hover { background: #4338ca; color: #fff; }

/* ── Filters Wrapper ───────────────────────────────────────── */
.filters-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 14px;
  margin-bottom: 1rem;
}

.filters-wrapper .filters-bar {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.filters-wrapper .btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  padding: 5px 12px;
}

.filters-wrapper .btn .material-symbols-outlined {
  font-size: 16px;
}

.hover-white:hover {
  background: #dc3545 !important;
  color: #fff !important;
}
</style>